import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTimes, faPen, faSave } from '@fortawesome/pro-solid-svg-icons';

import {
  startUpdateRecord,
  cancelUpdateRecord,
  handleSkipRecord,
  handleUpdateRecord
} from '../store/actions/run_records';
import { getCurrentStep } from '../store/record_table_ui_selectors';
import { getEditRow, getEditingRecord } from '../store/run_records_selectors';
import { recordStatuses } from '../preview/import_constants';

export function FixedRecordRow({
  allValidImports,
  currentStep,
  editingRecord,
  editRow,
  record,
  onCancelUpdateRecord,
  onStartUpdateRecord,
  onToggleSkipRecord,
  onUpdateRecord,
}) {
  const previewStep = () => currentStep === 'preview';

  const editingRow = () => {
    return (editingRecord.id === record.id && editRow);
  };

  const handleUpdateRecord = () => {
    if (!editingRow() && !editRow) {
      onStartUpdateRecord(record, true);
    }
  };

  const renderErrorMessage = (record) => {
    switch (record.previewStatus) {
    case recordStatuses['error']:
      return record.errorMessage;
    case recordStatuses['duplicate']:
      return `Duplicate of Row ${record.duplicateCsvRow}`;
    case recordStatuses['skipped']:
      return `Skipped`;
    }
  };

  const renderStatusIcon = (record) => {
    const statusToolTip = (
      <Tooltip id="status-tooltip" placement="top">
        <span>
          {renderErrorMessage(record)}
        </span>
      </Tooltip>
    );

    return (
      <OverlayTrigger overlay={statusToolTip} placement="top">
        <FontAwesomeIcon
          className={`${record.previewStatus}-icon`}
          icon={faInfoCircle}
          size="lg"
        />
      </OverlayTrigger>
    );
  };

  const renderPreviewFields = (record) => {
    return (
      <React.Fragment>
        <td className="cell-small table-cell">
          <input
            checked={!record.skip}
            className="checkbox--lg"
            type="checkbox"
            onChange={() => onToggleSkipRecord(record)}
          />
        </td>
        {!allValidImports &&
          <td className="cell-small table-cell text-center record-status">
            {record.previewStatus !== recordStatuses['success'] && renderStatusIcon(record)}
          </td>
        }
      </React.Fragment>
    );
  };

  const renderToolTip = (text) => {
    return (
      <Tooltip id="status-tooltip" placement="top">
        <span>
          {text}
        </span>
      </Tooltip>
    );
  };

  const renderSaveAndCancelIcons = () => {
    return (
      <React.Fragment>
        <OverlayTrigger overlay={(renderToolTip('Cancel'))} placement="top">
          <FontAwesomeIcon
            className="edit-icon ml-3 text-danger"
            icon={faTimes}
            size="lg"
            onClick={() => onCancelUpdateRecord()}
          />
        </OverlayTrigger>
        <OverlayTrigger overlay={(renderToolTip('Save'))} placement="top">
          <FontAwesomeIcon
            className="edit-icon ml-3 mr-3"
            icon={faSave}
            size="lg"
            onClick={() => onUpdateRecord()}
          />
        </OverlayTrigger>
      </React.Fragment>
    );
  };

  const renderEditIcon = (id) => {
    const iconClass = classNames({
      'edit-icon': true,
      'ml-3': true,
      'mr-3': true,
      'text-muted': !editingRow(id) && editRow
    });

    return (
      <OverlayTrigger overlay={(renderToolTip('Edit'))} placement="top">
        <FontAwesomeIcon
          className={iconClass}
          icon={faPen}
          onClick={() => handleUpdateRecord(id)}
        />
      </OverlayTrigger>
    );
  };

  const renderReviewFields = () => {
    let status = record.status;

    if (status === 'created') {
      status = `New ${record.mapperTypeClassLabel} Created`;
    }

    const editingRow = editingRow();
    return (
      <React.Fragment>
        <td className={`cell-small table-cell text-capitalize ${record.status}-cell`}>
          {status}
        </td>
        <td className="table-cell p-0">
          {editingRow && renderSaveAndCancelIcons()}
          {(record.status === recordStatuses.failed && !editingRow) &&
            renderEditIcon()}
        </td>
      </React.Fragment>
    );
  };

  const renderPreviewOrReview = (record) => {
    if (previewStep()) {
      return renderPreviewFields(record);
    } else {
      return renderReviewFields();
    }
  };

  return (
    <tr className={`${record.previewStatus}-row`} key={record.id + 'fixed'}>
      <td className="cell-small text-center table-cell table-cell-dark">{record.index}</td>
      {renderPreviewOrReview(record)}
    </tr>
  );
}

FixedRecordRow.propTypes = {
  allValidImports: PropTypes.bool,
  currentStep: PropTypes.string.isRequired,
  editingRecord: PropTypes.object.isRequired,
  editRow: PropTypes.bool,
  record: PropTypes.shape({
    duplicateCsvRow: PropTypes.number,
    errorMessage: PropTypes.string,
    id: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    previewStatus: PropTypes.string,
    skip: PropTypes.bool.isRequired,
  }).isRequired,
  onCancelUpdateRecord: PropTypes.func.isRequired,
  onStartUpdateRecord: PropTypes.func.isRequired,
  onToggleSkipRecord: PropTypes.func.isRequired,
  onUpdateRecord: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  currentStep: getCurrentStep(state),
  editRow: getEditRow(state),
  editingRecord: getEditingRecord(state),
});

const mapDispatchToProps = {
  onToggleSkipRecord: handleSkipRecord,
  onCancelUpdateRecord: cancelUpdateRecord,
  onStartUpdateRecord: startUpdateRecord,
  onUpdateRecord: handleUpdateRecord
};

export default connect(mapStateToProps, mapDispatchToProps)(FixedRecordRow);
